import {
  ChatAnalyticsDTO,
  ChatAnalyticsQueryDTO,
  ChatHistoryDTO,
  ChatSessionDTO,
  PatchSessionDTO,
  ThumbsFeedbackDTO,
} from "@onetutor/shared/dto";
import { buildQueryString } from "../utils/api.util";
import { onetutor } from "./onetutor";

const chatApi = onetutor.injectEndpoints({
  endpoints: (builder) => ({
    getChatHistory: builder.query<
      ChatHistoryDTO,
      { courseId: string; sessionId: string }
    >({
      query: ({ courseId, sessionId }) => ({
        url: `course/${courseId}/chat/${sessionId}`,
      }),
      providesTags: (_result, _error, { sessionId }) => [
        { type: "chat", id: sessionId },
      ],
    }),
    postNewChat: builder.mutation<ChatSessionDTO, { courseId: string }>({
      query: ({ courseId }) => ({
        url: `course/${courseId}/chat`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { courseId }) => [
        { type: "chat", id: `${courseId}-LIST` },
      ],
    }),
    patchChatSession: builder.mutation<
      void,
      { sessionId: string; courseId: string; body: PatchSessionDTO }
    >({
      query: ({ sessionId, courseId, body }) => ({
        url: `course/${courseId}/chat/${sessionId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { sessionId, courseId }) => [
        { type: "chat", id: sessionId },
        { type: "chat", id: `${courseId}-LIST` },
      ],
    }),
    deleteChatSession: builder.mutation<
      void,
      { sessionId: string; courseId: string }
    >({
      query: ({ sessionId, courseId }) => ({
        url: `course/${courseId}/chat/${sessionId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { sessionId, courseId }) => [
        { type: "chat", id: sessionId },
        { type: "chat", id: `${courseId}-LIST` },
      ],
    }),
    getChatSessions: builder.query<ChatSessionDTO[], string>({
      query: (courseId) => ({
        url: `course/${courseId}/chat`,
      }),
      providesTags: (result, _error, courseId) => [
        ...(result ?? []).map((session) => ({
          type: "chat" as const,
          id: session.id,
        })),
        { type: "chat", id: `${courseId}-LIST` },
      ],
    }),
    postNewFeedback: builder.mutation<
      void,
      { courseId: string; messageId: string; feedback: ThumbsFeedbackDTO }
    >({
      query: ({ courseId, messageId, feedback }) => ({
        url: `course/${courseId}/chat/message/${messageId}/feedback`,
        method: "POST",
        body: feedback,
      }),
    }),
    getChatAnalytics: builder.query<
      ChatAnalyticsDTO[],
      { courseId: string } & ChatAnalyticsQueryDTO
    >({
      query: ({ courseId, date, type }) =>
        buildQueryString(`course/${courseId}/analytics`, {
          date: date.toISOString(),
          type,
        }),
      providesTags: (_result, _error, { courseId }) => [
        { type: "chat", id: `analytics-${courseId}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  usePostNewChatMutation,
  useGetChatHistoryQuery,
  useLazyGetChatHistoryQuery,
  usePatchChatSessionMutation,
  useDeleteChatSessionMutation,
  useGetChatSessionsQuery,
  usePostNewFeedbackMutation,
  useGetChatAnalyticsQuery,
} = chatApi;
